const VERSION = 2;
const LEGACY_VERSION = 1;
const STORAGE_KEY = 'zoni_marketing_attribution_v2';
const LEGACY_STORAGE_KEY = 'zoni_marketing_attribution_v1';
const SESSION_FINGERPRINT_KEY = 'zoni_marketing_attribution_session_fingerprints_v2';
const HANDOFF_PARAM = 'zoni_attr';
const RETENTION_MS = 90 * 24 * 60 * 60 * 1000;
const MAX_TOUCHES = 20;
const MAX_ENVELOPE_BYTES = 32 * 1024;
const MAX_HANDOFF_LENGTH = 6000;

const ALLOWED_HOSTS = new Set([
    'zoni.edu', 'www.zoni.edu', 'highschool.zoni.edu',
    'zonilive.com', 'www.zonilive.com',
    'zonieducation.com', 'www.zonieducation.com',
    'zoniteachertraining.com', 'www.zoniteachertraining.com',
    'zoniacademy.com', 'www.zoniacademy.com',
]);

const PARAM_FIELDS = {
    utm_id: 'utm_id',
    utm_source: 'utm_source',
    utm_medium: 'utm_medium',
    utm_campaign: 'utm_campaign',
    utm_term: 'utm_term',
    utm_content: 'utm_content',
    utm_source_platform: 'utm_source_platform',
    utm_creative_format: 'utm_creative_format',
    utm_marketing_tactic: 'utm_marketing_tactic',
    gclid: 'gclid',
    gbraid: 'gbraid',
    wbraid: 'wbraid',
    gad_source: 'gad_source',
    gad_campaignid: 'google_campaign_id',
    campaignid: 'google_campaign_id',
    adgroupid: 'google_ad_group_id',
    creative: 'google_creative_id',
    keyword: 'keyword',
    matchtype: 'match_type',
    network: 'network',
    device: 'device',
    placement: 'placement',
};

const TOUCH_FIELDS = new Set([
    'touch_id', 'captured_at', 'landing_page_url', 'referrer_url',
    ...Object.values(PARAM_FIELDS),
]);
const EVIDENCE_FIELDS = new Set(Object.values(PARAM_FIELDS));
const CLICK_ID_FIELDS = ['gclid', 'gbraid', 'wbraid'];
const HANDOFF_OPTIONAL_FIELDS = [
    'landing_page_url', 'referrer_url', 'utm_term', 'utm_content', 'keyword',
    'placement', 'utm_creative_format', 'utm_marketing_tactic',
];
const HANDOFF_IDENTITY_FIELDS = new Set([
    'touch_id', 'captured_at', 'gclid', 'gbraid', 'wbraid',
    'google_campaign_id', 'google_ad_group_id', 'google_creative_id',
]);
let currentAttribution = null;
let initialized = false;

function byteLength(value) {
    return new TextEncoder().encode(JSON.stringify(value)).byteLength;
}

function fieldLimit(key) {
    if (key.endsWith('_url')) return 2048;
    if (CLICK_ID_FIELDS.includes(key)) return 512;
    if (['keyword', 'placement'].includes(key)) return 500;
    return 140;
}

function sanitizedUrl(value) {
    try {
        const url = new URL(value);
        if (!['http:', 'https:'].includes(url.protocol)) return '';
        return `${url.origin}${url.pathname}`.slice(0, 2048);
    } catch (_) {
        return '';
    }
}

function touchId() {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') {
        return window.crypto.randomUUID();
    }
    return `touch-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

function normalizeTouch(raw) {
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
    if (Object.keys(raw).some((key) => !TOUCH_FIELDS.has(key))) return null;
    if (!/^[A-Za-z0-9._~-]{8,64}$/.test(raw.touch_id || '')) return null;
    const captured = Date.parse(raw.captured_at || '');
    if (!Number.isFinite(captured) || captured > Date.now() + 300000 || captured < Date.now() - RETENTION_MS) return null;

    const touch = {};
    for (const [key, value] of Object.entries(raw)) {
        if (typeof value !== 'string' || !value.trim()) continue;
        touch[key] = value.trim().slice(0, fieldLimit(key));
    }
    if (![...EVIDENCE_FIELDS].some((field) => touch[field])) return null;
    touch.captured_at = new Date(captured).toISOString();
    if (touch.landing_page_url) touch.landing_page_url = sanitizedUrl(touch.landing_page_url);
    if (touch.referrer_url) touch.referrer_url = sanitizedUrl(touch.referrer_url);
    for (const field of ['google_campaign_id', 'google_ad_group_id', 'google_creative_id']) {
        if (touch[field] && !/^\d+$/.test(touch[field])) return null;
    }
    return touch;
}

function sameTouch(left, right) {
    if (left.touch_id === right.touch_id) return true;
    return CLICK_ID_FIELDS.some((field) => left[field] && left[field] === right[field]);
}

function mergeTouch(stored, incoming) {
    const merged = { ...stored };
    for (const [field, value] of Object.entries(incoming)) {
        if (!merged[field]) merged[field] = value;
    }
    return merged;
}

function buildEnvelope(rawTouches) {
    const touches = [];
    for (const rawTouch of rawTouches) {
        const touch = normalizeTouch(rawTouch);
        if (!touch) continue;
        const matchIndexes = touches.flatMap((stored, index) => sameTouch(stored, touch) ? [index] : []);
        if (!matchIndexes.length) {
            touches.push(touch);
        } else {
            const firstIndex = matchIndexes[0];
            let merged = { ...touches[firstIndex] };
            for (const index of matchIndexes.slice(1)) merged = mergeTouch(merged, touches[index]);
            touches[firstIndex] = mergeTouch(merged, touch);
            for (const index of matchIndexes.slice(1).reverse()) touches.splice(index, 1);
        }
    }
    touches.sort((left, right) => Date.parse(left.captured_at) - Date.parse(right.captured_at));
    while (touches.length > MAX_TOUCHES) touches.splice(1, 1);
    if (!touches.length) return null;

    const makeEnvelope = () => ({
        version: VERSION,
        first_touch: { ...touches[0] },
        latest_touch: { ...touches[touches.length - 1] },
        touches: touches.map((touch) => ({ ...touch })),
    });
    let envelope = makeEnvelope();
    while (byteLength(envelope) > MAX_ENVELOPE_BYTES && touches.length > 2) {
        touches.splice(1, 1);
        envelope = makeEnvelope();
    }
    return byteLength(envelope) <= MAX_ENVELOPE_BYTES ? envelope : null;
}

function normalizeEnvelope(raw) {
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
    try {
        if (byteLength(raw) > MAX_ENVELOPE_BYTES) return null;
    } catch (_) {
        return null;
    }
    if (raw.version === LEGACY_VERSION) {
        if (Object.keys(raw).some((key) => !['version', 'first_touch', 'latest_touch'].includes(key))) return null;
        const first = normalizeTouch(raw.first_touch);
        const latest = normalizeTouch(raw.latest_touch);
        if (!first && !latest) return null;
        if (first && latest && sameTouch(first, latest)) return buildEnvelope([mergeTouch(latest, first)]);
        return buildEnvelope([first, latest].filter(Boolean));
    }
    if (raw.version !== VERSION || !Array.isArray(raw.touches) || !raw.touches.length || raw.touches.length > MAX_TOUCHES) return null;
    if (Object.keys(raw).some((key) => !['version', 'first_touch', 'latest_touch', 'touches'].includes(key))) return null;
    if (!normalizeTouch(raw.first_touch) || !normalizeTouch(raw.latest_touch)) return null;
    const touches = raw.touches.map(normalizeTouch);
    return touches.every(Boolean) ? buildEnvelope(touches) : null;
}

function mergeEnvelopes(...envelopes) {
    return buildEnvelope(envelopes.filter(Boolean).flatMap((envelope) => envelope.touches));
}

function encodeEnvelope(envelope) {
    const bytes = new TextEncoder().encode(JSON.stringify(envelope));
    let binary = '';
    bytes.forEach((byte) => { binary += String.fromCharCode(byte); });
    return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function handoffEnvelope(envelope) {
    let candidate = buildEnvelope(envelope.touches);
    if (!candidate || encodeEnvelope(candidate).length <= MAX_HANDOFF_LENGTH) return candidate;

    const compactTouches = envelope.touches.map((touch) => {
        const compact = { ...touch };
        for (const field of HANDOFF_OPTIONAL_FIELDS) delete compact[field];
        return compact;
    });
    candidate = buildEnvelope(compactTouches);
    while (candidate && encodeEnvelope(candidate).length > MAX_HANDOFF_LENGTH && candidate.touches.length > 2) {
        candidate = buildEnvelope([candidate.touches[0], ...candidate.touches.slice(2)]);
    }
    if (candidate && encodeEnvelope(candidate).length > MAX_HANDOFF_LENGTH) {
        const identityTouches = envelope.touches.map((touch) => {
            const compact = Object.fromEntries(
                Object.entries(touch).filter(([field]) => HANDOFF_IDENTITY_FIELDS.has(field))
            );
            if (![...EVIDENCE_FIELDS].some((field) => compact[field])) {
                const evidenceField = [...EVIDENCE_FIELDS].find((field) => touch[field]);
                if (evidenceField) compact[evidenceField] = touch[evidenceField];
            }
            return compact;
        });
        candidate = buildEnvelope(identityTouches);
        while (candidate && encodeEnvelope(candidate).length > MAX_HANDOFF_LENGTH && candidate.touches.length > 2) {
            candidate = buildEnvelope([candidate.touches[0], ...candidate.touches.slice(2)]);
        }
    }
    return candidate && encodeEnvelope(candidate).length <= MAX_HANDOFF_LENGTH ? candidate : null;
}

function decodeEnvelope(value) {
    if (!value || value.length > MAX_HANDOFF_LENGTH) return null;
    try {
        const padded = value.replace(/-/g, '+').replace(/_/g, '/') + '==='.slice((value.length + 3) % 4);
        const binary = atob(padded);
        const bytes = Uint8Array.from(binary, (character) => character.charCodeAt(0));
        return normalizeEnvelope(JSON.parse(new TextDecoder().decode(bytes)));
    } catch (_) {
        return null;
    }
}

function loadStored() {
    for (const key of [STORAGE_KEY, LEGACY_STORAGE_KEY]) {
        try {
            const stored = JSON.parse(localStorage.getItem(key) || 'null');
            if (!stored || stored.expires_at <= Date.now()) {
                localStorage.removeItem(key);
                continue;
            }
            const envelope = normalizeEnvelope(stored.attribution);
            if (envelope) return envelope;
            localStorage.removeItem(key);
        } catch (_) {
            localStorage.removeItem(key);
        }
    }
    return null;
}

function saveStored(envelope) {
    if (!envelope) return;
    const captured = Date.parse(envelope.first_touch.captured_at);
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify({
            expires_at: captured + RETENTION_MS,
            attribution: envelope,
        }));
        localStorage.removeItem(LEGACY_STORAGE_KEY);
    } catch (_) {
        // Storage can be disabled; the current page still receives attribution.
    }
}

function sessionFingerprint(touch) {
    return JSON.stringify([...EVIDENCE_FIELDS]
        .filter((field) => !CLICK_ID_FIELDS.includes(field))
        .sort()
        .map((field) => [field, touch[field] || ''])
        .concat([['landing_page_url', touch.landing_page_url || '']]));
}

function isRepeatedSessionTouch(touch) {
    if (CLICK_ID_FIELDS.some((field) => touch[field])) return false;
    const fingerprint = sessionFingerprint(touch);
    try {
        const seen = JSON.parse(sessionStorage.getItem(SESSION_FINGERPRINT_KEY) || '[]');
        if (Array.isArray(seen) && seen.includes(fingerprint)) return true;
        const updated = Array.isArray(seen) ? seen.slice(-99) : [];
        updated.push(fingerprint);
        sessionStorage.setItem(SESSION_FINGERPRINT_KEY, JSON.stringify(updated));
    } catch (_) {
        return currentAttribution?.latest_touch
            ? sessionFingerprint(currentAttribution.latest_touch) === fingerprint
            : false;
    }
    return false;
}

function captureCurrentTouch() {
    const params = new URLSearchParams(window.location.search);
    const touch = {};
    for (const [parameter, field] of Object.entries(PARAM_FIELDS)) {
        const value = (params.get(parameter) || '').trim();
        if (value) touch[field] = value;
    }
    if (!Object.keys(touch).length) return null;
    touch.touch_id = touchId();
    touch.captured_at = new Date().toISOString();
    touch.landing_page_url = sanitizedUrl(window.location.href);
    touch.referrer_url = sanitizedUrl(document.referrer);
    const normalized = normalizeTouch(touch);
    return normalized && !isRepeatedSessionTouch(normalized) ? normalized : null;
}

function importHandoff() {
    const url = new URL(window.location.href);
    const envelope = decodeEnvelope(url.searchParams.get(HANDOFF_PARAM));
    if (url.searchParams.has(HANDOFF_PARAM)) {
        url.searchParams.delete(HANDOFF_PARAM);
        history.replaceState(history.state, '', `${url.pathname}${url.search}${url.hash}`);
    }
    return envelope;
}

function decorateLink(anchor) {
    if (!currentAttribution || !anchor || anchor.hasAttribute('download')) return;
    const href = anchor.getAttribute('href') || '';
    if (/^(?:mailto:|tel:|javascript:|#)/i.test(href)) return;
    try {
        const url = new URL(href, window.location.href);
        const destinationHost = url.hostname.toLowerCase();
        if (!['http:', 'https:'].includes(url.protocol) || !ALLOWED_HOSTS.has(destinationHost) || destinationHost === window.location.hostname.toLowerCase()) return;
        const handoff = handoffEnvelope(currentAttribution);
        if (!handoff) return;
        url.searchParams.set(HANDOFF_PARAM, encodeEnvelope(handoff));
        anchor.href = url.toString();
    } catch (_) {
        // Ignore invalid and non-navigation hrefs.
    }
}

export function initializeMarketingAttribution() {
    if (typeof window === 'undefined') return null;
    if (initialized) return currentAttribution;
    initialized = true;
    const stored = loadStored();
    const imported = importHandoff();
    currentAttribution = mergeEnvelopes(stored, imported);
    const campaignTouch = captureCurrentTouch();
    if (campaignTouch) currentAttribution = buildEnvelope([...(currentAttribution?.touches || []), campaignTouch]);
    if (currentAttribution) saveStored(currentAttribution);

    const decorateAll = () => document.querySelectorAll('a[href]').forEach(decorateLink);
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', decorateAll, { once: true });
    else decorateAll();
    document.addEventListener('click', (event) => decorateLink(event.target.closest?.('a[href]')), true);
    return currentAttribution;
}

export function getMarketingAttribution() {
    return currentAttribution ? JSON.parse(JSON.stringify(currentAttribution)) : null;
}

initializeMarketingAttribution();
